import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ROUTES, buildRoute } from '../../constants/routes';
import { useListOffersQuery } from '../../store/siteContentApi';
import { computeFinal, shortDiscount } from '../../utils/offerLookup';
import HomeSpecialsStripShimmer from '../shimmer/HomeSpecialsStripShimmer';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Deterministic emoji fallback when the admin hasn't set one, keyed off
// the campaign or test name. Mirrors the list on the Specials page.
const emojiFor = (o) => {
  const key = (o?.campaign || o?.test_name || '').toLowerCase();
  if (key.includes('women'))    return '🌸';
  if (key.includes('summer'))   return '☀️';
  if (key.includes('diabet'))   return '🔬';
  if (key.includes('heart') || key.includes('cardi')) return '❤️';
  if (key.includes('thyroid'))  return '🦋';
  if (key.includes('vitamin'))  return '💊';
  return '🧪';
};

// Earliest validity end across the visible offers — drives the "Ends DD MMM"
// urgency line in the header. Returns null when no offer has an end date,
// in which case we render "Limited period" instead.
const earliestValidityLabel = (offers) => {
  const dates = offers
    .map((o) => (o.valid_until ? new Date(o.valid_until).getTime() : null))
    .filter((t) => t && !Number.isNaN(t));
  if (!dates.length) return 'Limited period';
  const d = new Date(Math.min(...dates));
  return `Ends ${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`;
};

const SpecialsStrip = () => {
  const navigate = useNavigate();
  const { data: offers = [], isLoading } = useListOffersQuery();

  // Show only the first 4 in-window offers — no campaign grouping, no
  // category tabs, no auto-rotate. Backend already returns them ordered
  // by campaign + sort_order + createdAt, so the first 4 are the most
  // editorially intentional set of cards.
  const visibleOffers = useMemo(() => offers.slice(0, 4), [offers]);
  const validityLabel = useMemo(() => earliestValidityLabel(visibleOffers), [visibleOffers]);

  if (isLoading) return <HomeSpecialsStripShimmer cards={4} />;
  if (visibleOffers.length === 0) return null;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        py: { xs: 5, md: 7 },
        borderTop: '4px solid var(--forest)',
        borderBottom: '1px solid var(--border-l)',
      }}
    >
      <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
        {/* Header — title block left, "View all offers" CTA right.
            No tab strip — the cards below are the four offers, full stop. */}
        <Grid
          container
          columns={12}
          columnSpacing={2}
          rowSpacing={2}
          alignItems="center"
          sx={{ mb: 3.5 }}
        >
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                lineHeight: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: '#25D366',
                  borderRadius: '50%',
                  boxShadow: '0 0 6px rgba(37,211,102,0.5)',
                  flexShrink: 0,
                }}
              />
              <Typography
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: 'var(--amber)',
                  lineHeight: 1,
                }}
              >
                Limited Time Offers
              </Typography>
            </Box>
            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
                fontWeight: 600,
                fontStyle: 'italic',
                color: 'var(--forest)',
                lineHeight: 1.15,
                mb: 0.75,
              }}
            >
              Save on doctor-curated tests
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--stone)', fontWeight: 300 }}>
              {validityLabel} &nbsp;·&nbsp; Home collection included &nbsp;·&nbsp; Reports in 6 hrs
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ marginLeft: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                width: '100%',
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(ROUTES.SPECIALS)}
                sx={{
                  borderRadius: 100,
                  bgcolor: 'var(--forest)',
                  color: '#fff',
                  px: 2.25,
                  py: 0.875,
                  fontSize: 12.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'var(--ink)' },
                }}
              >
                View all offers →
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Cards — fixed 4-col grid. When fewer than 4 offers exist,
            trailing cells just stay empty (responsive: 2-col on xs, 3-col on sm). */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(3, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 2,
            width: '100%',
          }}
        >
          {visibleOffers.map((o) => {
            const finalPrice = computeFinal(o);
            const saveBadge = o.badge || shortDiscount(o);
            return (
              <Box
                key={o._id}
                onClick={() => navigate(buildRoute(ROUTES.TEST_DETAIL, { id: o.test_id }))}
                sx={{
                  bgcolor: 'var(--cream)',
                  border: '1.5px solid var(--border-l)',
                  borderRadius: '18px',
                  p: { xs: 2, md: 2.5 },
                  pt: { xs: 2.25, md: 2.75 },
                  cursor: 'pointer',
                  transition: 'all 0.25s',
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 6,
                    bgcolor: 'var(--forest)',
                    borderTopLeftRadius: '18px',
                    borderTopRightRadius: '18px',
                  },
                  '&:hover': {
                    borderColor: 'var(--sage-p)',
                    boxShadow: '0 10px 30px rgba(61,122,82,0.12)',
                    transform: 'translateY(-5px)',
                  },
                }}
              >
                <Typography sx={{ fontSize: 28, mb: 1.5, lineHeight: 1 }}>{emojiFor(o)}</Typography>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', mb: 0.5, lineHeight: 1.3 }}
                >
                  {o.title || o.test_name}
                </Typography>
                {o.description && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: 'var(--sage)',
                      fontWeight: 400,
                      mb: 1.75,
                      lineHeight: 1.4,
                      flexGrow: 1,
                    }}
                  >
                    {o.description}
                  </Typography>
                )}
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.25, mt: o.description ? 0 : 'auto' }}>
                  <Typography
                    className="serif"
                    sx={{ fontSize: 26, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}
                  >
                    {fmtINR(finalPrice)}
                  </Typography>
                  {Number(o.original_price) > 0 && finalPrice < Number(o.original_price) && (
                    <Typography
                      sx={{
                        fontSize: 12,
                        textDecoration: 'line-through',
                        color: 'var(--stone)',
                        fontWeight: 400,
                      }}
                    >
                      {fmtINR(o.original_price)}
                    </Typography>
                  )}
                </Stack>
                <Box
                  sx={{
                    display: 'block',
                    width: '100%',
                    fontSize: 11,
                    fontWeight: 800,
                    color: '#fff',
                    bgcolor: 'var(--forest)',
                    py: 0.75,
                    borderRadius: 100,
                    textAlign: 'center',
                    letterSpacing: '0.04em',
                  }}
                >
                  {saveBadge}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default SpecialsStrip;
